import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Chip, Dialog, Select, Tooltip } from '@nyce/admin-kit/ui';
import type { AuditRecord } from '../../api';
import { api } from '../../session';
import { EmptyState, ErrorBox, Loading, Page, TableScroller } from '../../components/common';
import { errorText } from '../../components/registry';

export const AUDIT_PAGE = 100;

/** Target types the API writes today; types seen in loaded records are added to the filter too. */
const KNOWN_TYPES = [
  'cluster',
  'db_server',
  'environment',
  'link',
  'request',
  'pod',
  'deployment',
  'job',
  'auth_tenant',
  'auth_customer',
  'scim_endpoint',
];

export function relativeTime(iso: string, now = Date.now()): string {
  const s = Math.round((now - new Date(iso).getTime()) / 1000);
  if (!isFinite(s)) return '';
  const abs = Math.abs(s);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (abs < 60) return rtf.format(-s, 'second');
  if (abs < 3600) return rtf.format(-Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf.format(-Math.round(s / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(-Math.round(s / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(-Math.round(s / (86400 * 30)), 'month');
  return rtf.format(-Math.round(s / (86400 * 365)), 'year');
}

const parse = (text: string | null): unknown => {
  if (text == null || text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Top-level keys whose value differs between `before` and `after` (added and removed keys included). */
export function changedKeys(before: unknown, after: unknown): Set<string> {
  const out = new Set<string>();
  if (!isObject(before) || !isObject(after)) return out;
  for (const k of new Set([...Object.keys(before), ...Object.keys(after)]))
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) out.add(k);
  return out;
}

/** Pretty JSON with every top-level key on its own block so changed keys can be highlighted. */
function JsonView({ value, changed, label }: { value: unknown; changed: Set<string>; label: string }) {
  if (value == null) return <div className="oph-json oph-json-empty">— none —</div>;
  if (!isObject(value))
    return (
      <pre className="oph-json" aria-label={label}>
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      </pre>
    );
  const keys = Object.keys(value);
  return (
    <pre className="oph-json" aria-label={label}>
      {'{\n'}
      {keys.map((k, i) => (
        <span key={k} className={changed.has(k) ? 'oph-json-changed' : undefined} data-key={k}>
          {`  ${JSON.stringify(k)}: ${JSON.stringify(value[k], null, 2).replace(/\n/g, '\n  ')}${
            i < keys.length - 1 ? ',' : ''
          }\n`}
        </span>
      ))}
      {'}'}
    </pre>
  );
}

function OutcomeChip({ outcome }: { outcome: string }) {
  return (
    <Chip size="sm" className={`oph-outcome oph-outcome-${outcome}`}>
      {outcome}
    </Chip>
  );
}

function RecordDialog({ record, onClose }: { record: AuditRecord; onClose: () => void }) {
  const before = parse(record.before);
  const after = parse(record.after);
  const changed = changedKeys(before, after);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${record.action} ${record.targetType} ${record.targetId}`}
      maxWidth={980}
    >
      <div className="oph-audit-meta">
        <OutcomeChip outcome={record.outcome} />
        <span>{new Date(record.occurredAt).toLocaleString()}</span>
        <span>
          {record.userName || record.userSub}
          {record.userName && record.userSub && <span className="nyk-ui-hint"> ({record.userSub})</span>}
        </span>
      </div>
      {record.detail && <p className="oph-audit-detail">{record.detail}</p>}
      {changed.size > 0 && (
        <p className="nyk-ui-hint" data-testid="changed-keys">
          Changed: {[...changed].join(', ')}
        </p>
      )}
      <div className="oph-diff">
        <div>
          <div className="nyk-ui-eyebrow">Before</div>
          <JsonView value={before} changed={changed} label="Before" />
        </div>
        <div>
          <div className="nyk-ui-eyebrow">After</div>
          <JsonView value={after} changed={changed} label="After" />
        </div>
      </div>
    </Dialog>
  );
}

export function AuditPage() {
  // the filter lives in the URL so the dashboards can link to a filtered log
  const [params, setParams] = useSearchParams();
  const targetType = params.get('targetType') ?? '';
  const setTargetType = (t: string) => setParams(t ? { targetType: t } : {}, { replace: true });
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState<AuditRecord | null>(null);
  const seq = useRef(0);

  const load = useCallback(
    async (before?: number) => {
      const id = ++seq.current;
      setLoading(true);
      try {
        const page = await api.admin.audit({
          limit: AUDIT_PAGE,
          before,
          targetType: targetType || undefined,
        });
        if (id !== seq.current) return;
        setRecords((cur) => (before === undefined ? page : [...cur, ...page]));
        setMore(page.length >= AUDIT_PAGE);
        setError(null);
      } catch (e) {
        if (id === seq.current) setError(errorText(e));
      } finally {
        if (id === seq.current) setLoading(false);
      }
    },
    [targetType]
  );
  useEffect(() => {
    void load();
  }, [load]);

  const types = useMemo(
    () => [...new Set([...KNOWN_TYPES, ...records.map((r) => r.targetType).filter(Boolean)])].sort(),
    [records]
  );
  const now = Date.now();

  return (
    <Page
      title="Audit"
      description="Every write made through the hub, including denied requests. Click a row for the before/after."
      actions={
        <Select
          aria-label="Target type"
          value={targetType}
          onChange={setTargetType}
          options={[{ value: '', label: 'All targets' }, ...types.map((t) => ({ value: t, label: t }))]}
        />
      }
    >
      <ErrorBox error={error} />
      {!loading && !error && !records.length && <EmptyState card title="No audit records" />}
      {!!records.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards oph-audit" data-testid="audit-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Outcome</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr
                    key={r.id}
                    data-row={r.id}
                    className="nyk-clickable"
                    tabIndex={0}
                    onClick={() => setOpen(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen(r);
                      }
                    }}
                  >
                    <td data-label="Time" className="nyk-nowrap">
                      <Tooltip text={relativeTime(r.occurredAt, now)}>
                        <span>{new Date(r.occurredAt).toLocaleString()}</span>
                      </Tooltip>
                    </td>
                    <td data-label="User">{r.userName || r.userSub}</td>
                    <td data-label="Action">{r.action}</td>
                    <td data-label="Target">
                      <span className="nyk-ui-hint">{r.targetType}</span>
                      {r.targetId && <span className="nyk-url"> {r.targetId}</span>}
                    </td>
                    <td data-label="Outcome">
                      <OutcomeChip outcome={r.outcome} />
                    </td>
                    <td data-label="Detail" className="oph-audit-detail-cell">
                      {r.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      <Loading loading={loading} />
      {more && !loading && (
        <div className="nyk-pager">
          <Button variant="secondary" onClick={() => void load(records[records.length - 1]?.id)}>
            Load older
          </Button>
        </div>
      )}
      {open && <RecordDialog record={open} onClose={() => setOpen(null)} />}
    </Page>
  );
}
